import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, IconeTelecharger } from './charte/Elements';
import { COULEURS } from '../theme/charte';
import { DocumentClient, formatMontant } from '../donnees/documents';
import { idMission, Mission, missionSignee } from '../donnees/missions';
import { dernierDevis, jourCourt } from '../donnees/situation';

/* Liste de documents en grille, d'après la maquette Documents : numéro
   (JetBrains Mono) et nature, mission, date, montant TTC (sans unité : la
   page l'annonce en F CFA (XOF)), état en pastille, actions (régler,
   signer, PDF). Sert à la page Documents et à l'onglet Documents de la
   fiche de mission. */

export interface EtatDocument { libelle: string; couleur: string; aSigner?: boolean; aRegler?: boolean }

const nombre = (n: number) => Math.round(n).toLocaleString('fr-FR').replace(/ /g, ' ');

export function natureDocument(d: DocumentClient): string {
  switch (d.type) {
    case 'DEVIS': return 'Devis';
    case 'CONTRAT': return 'Contrat';
    case 'AVOIR': return d.documentOrigineNumero ? `Avoir sur ${d.documentOrigineNumero}` : 'Avoir';
    case 'FACTURE':
      return d.natureFacture === 'ACOMPTE' ? "Facture d'acompte" : d.natureFacture === 'SOLDE' ? 'Facture · solde' : 'Facture · totalité';
    default: return d.type;
  }
}

/** État affiché d'un document, selon les couleurs de la charte. */
export function etatDocument(d: DocumentClient, mission: Mission | undefined, documents: DocumentClient[]): EtatDocument {
  if (d.type === 'FACTURE') {
    switch (d.etatPaiement) {
      case 'NON_PAYEE': return { libelle: d.dateEcheance ? `À régler · ${jourCourt(d.dateEcheance)}` : 'À régler', couleur: COULEURS.attention, aRegler: true };
      case 'PARTIELLEMENT_PAYEE': return { libelle: 'Partiellement réglée', couleur: COULEURS.info, aRegler: true };
      case 'PAYEE': return { libelle: 'Réglée', couleur: COULEURS.succes };
      case 'TROP_PERCUE': return { libelle: 'Trop-perçue', couleur: COULEURS.erreur };
      case 'ANNULEE': return { libelle: 'Annulée par avoir', couleur: COULEURS.neutre };
      default: return { libelle: 'Émise', couleur: COULEURS.neutre };
    }
  }
  if (d.type === 'AVOIR') return { libelle: 'Imputé', couleur: COULEURS.neutre };
  if (d.type === 'CONTRAT') return missionSignee(mission) ? { libelle: 'Signé', couleur: COULEURS.succes } : { libelle: 'Émis', couleur: COULEURS.neutre };
  if (d.type === 'DEVIS') {
    const trace = mission?.signatureDevis;
    if (trace && trace.devisNumero === d.numero) return { libelle: `Signé le ${jourCourt(trace.signeLe)}`, couleur: COULEURS.succes };
    const dernier = d.questId ? dernierDevis(documents, d.questId) : null;
    if (dernier && dernier.id !== d.id) return { libelle: 'Remplacé', couleur: COULEURS.neutre };
    if (missionSignee(mission)) return { libelle: 'Signé', couleur: COULEURS.succes };
    if (mission) return { libelle: 'À signer', couleur: COULEURS.action, aSigner: true };
    return { libelle: 'Émis', couleur: COULEURS.neutre };
  }
  return { libelle: '—', couleur: COULEURS.neutre };
}

const ListeDocuments: React.FC<{
  documents: DocumentClient[];
  /** Toutes les missions du client (titres, signature) ; la colonne Mission n'apparaît que si avecMission. */
  missions: Mission[];
  avecMission?: boolean;
  pdfEnCours: string | null;
  onPdf: (d: DocumentClient) => void;
  /** Action de signature d'un devis : bouton (page Documents) ou lien vers le devis (fiche). */
  onSigner?: (d: DocumentClient) => void;
  lienReglement?: string;
  vide?: React.ReactNode;
}> = ({ documents, missions, avecMission = true, pdfEnCours, onPdf, onSigner, lienReglement = '/documents#comment-regler', vide }) => {
  const parId = new Map(missions.map(m => [idMission(m), m]));
  const colonnes = avecMission
    ? 'minmax(0, 1.3fr) minmax(0, 2fr) minmax(0, 0.9fr) minmax(0, 1.1fr) minmax(0, 1.2fr) minmax(0, 1fr)'
    : 'minmax(0, 1.4fr) minmax(0, 0.9fr) minmax(0, 1.1fr) minmax(0, 1.3fr) minmax(0, 1.2fr)';
  return (
    <div className="liste" style={{ '--colonnes': colonnes } as React.CSSProperties}>
      <div className="liste__entete" aria-hidden>
        <div>Numéro</div>{avecMission && <div>Mission</div>}<div>Date</div><div>Montant TTC</div><div>État</div><div />
      </div>
      {documents.length === 0 ? <div className="liste__vide">{vide ?? 'Aucun document.'}</div> : documents.map(d => {
        const mission = d.questId ? parId.get(d.questId) : undefined;
        const etat = etatDocument(d, mission, documents);
        const avoir = d.type === 'AVOIR' || d.montantTtc < 0;
        const occupe = pdfEnCours === String(d.id);
        return (
          <div key={d.id} className="liste__ligne liste__ligne--compacte" data-testid={`document-${d.id}`}>
            <div className="liste__principal" style={{ gap: '2px' }}>
              <span className="mono" style={{ fontSize: '13px' }}>{d.numero}</span>
              <span style={{ fontSize: '12px', color: COULEURS.texteDiscret }}>{natureDocument(d)}</span>
            </div>
            {avecMission && (
              <div data-label="Mission" style={{ fontSize: '14px', fontWeight: 500 }}>
                {mission ? <Link to={`/missions/${idMission(mission)}?onglet=documents`} style={{ color: COULEURS.texte }}>{mission.titre}</Link> : (d.titreMission || '—')}
              </div>
            )}
            <div data-label="Date" style={{ fontSize: '14px' }}>{jourCourt(d.emisLe)}</div>
            <div data-label="Montant TTC" className="mono" style={{ fontSize: '14px', color: avoir ? COULEURS.succes : undefined }}>
              {avoir ? `− ${nombre(Math.abs(d.montantTtc))}` : nombre(d.montantTtc)}
            </div>
            <div data-label="État"><Badge libelle={etat.libelle} couleur={etat.couleur} /></div>
            <div className="ligne" style={{ justifyContent: 'flex-end', gap: '8px', flexWrap: 'nowrap' }}>
              {etat.aSigner && (onSigner
                ? <button type="button" className="bouton bouton--petit" onClick={() => onSigner(d)}>Signer</button>
                : <Link to={`/documents#devis-${d.id}`} className="bouton bouton--petit">Signer ce devis</Link>)}
              {etat.aRegler && (
                <Link to={lienReglement} className="bouton bouton--secondaire bouton--petit">
                  {d.etatPaiement === 'PARTIELLEMENT_PAYEE' && d.resteDuTtc != null ? `Reste dû ${nombre(d.resteDuTtc)}` : 'Comment régler'}
                </Link>
              )}
              <button type="button" className="bouton bouton--icone" onClick={() => onPdf(d)} disabled={occupe}
                aria-label={`Télécharger le PDF ${d.numero}`} title="Télécharger le PDF">
                {occupe ? <span className="roue" aria-hidden /> : <IconeTelecharger />}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export { formatMontant };
export default ListeDocuments;
